import {
	EMPTY_ENTITY_DATA_VALIDATION,
	type EntityDataValidation,
	type FieldDefinition,
} from "@/services/form-definition";
import {
	API_DATE_ONLY_DATA_TYPE,
	API_DATETIME_DATA_TYPE,
	API_TIME_ONLY_DATA_TYPE,
} from "../constants/api-date-time-data-type.constant";
import {
	API_NUMBER_DATA_TYPES,
	normalizeApiNumberDataType,
} from "../constants/api-number-data-type.constant";
import { PALETTE_URL_PATTERN } from "../constants/palette-patterns.constant";
import type {
	CheckboxField,
	DatePickerField,
	DateTimePickerField,
	FieldType,
	FileUploadField,
	FormField,
	MultiSelectField,
	NumberField,
	PersistedDynamicFieldDefinition,
	RadioField,
	SelectField,
	TextField,
	TimePickerField,
	ToggleButtonField,
	UrlField,
} from "../interfaces/dynamicForm.interface";

/**
 * Optional layout context so `text` API rows map to the correct {@link FormField}
 * (`select`, `radio`, `text`) using `layoutJson` / `layoutInfo.fieldType`.
 * Boolean API rows map to checkbox.
 */
export interface PersistedDefinitionToFormFieldContext {
	layoutFieldTypeByDefinitionKey?: Map<string, FieldType>;
	/** Highest priority — e.g. the current layout `Field` node's `layoutInfo.fieldType`. */
	layoutFieldType?: FieldType;
}

function resolveLayoutFieldTypeFromContext(
	def: PersistedDynamicFieldDefinition,
	ctx?: PersistedDefinitionToFormFieldContext,
): FieldType | undefined {
	if (ctx?.layoutFieldType !== undefined) return ctx.layoutFieldType;
	const map = ctx?.layoutFieldTypeByDefinitionKey;
	if (!map) return undefined;
	return map.get(def.id) ?? map.get(def.name);
}

function isBooleanApiDataType(dataType: string): boolean {
	return dataType.toLowerCase() === "boolean";
}

function nonEmptyValidValues(
	rules: EntityDataValidation | null | undefined,
): string[] {
	const raw = rules?.validValues;
	if (!Array.isArray(raw)) return [];
	return raw.filter((v): v is string => typeof v === "string" && v.length > 0);
}

function booleanDataTypeToFormField(
	def: PersistedDynamicFieldDefinition,
): FormField {
	const label = def.label?.trim() ? def.label : def.name;
	const required = def.required ?? false;
	const disabled = def.disabled ?? false;
	const hidden = def.hidden ?? false;
	return {
		id: def.id,
		name: def.name,
		label,
		type: "checkbox",
		required,
		disabled,
		hidden,
		hint: def.hint,
		defaultChecked: def.defaultChecked ?? false,
	} satisfies CheckboxField;
}

/**
 * Maps a rich {@link FormField} to a row suitable for `fieldDefinitions` + API persistence.
 *
 * @param field - Field from the form editor projection or field factory.
 */
export function persistedFormFieldToDefinition(
	field: FormField,
): PersistedDynamicFieldDefinition {
	const base: FieldDefinition = {
		id: field.id,
		name: field.name,
		label: field.label,
		dataType: field.type,
		required: field.required ?? false,
		validationRules: null,
	};

	if (field.type === "text") {
		const tf = field as TextField;
		const pattern = tf.validation?.pattern;
		const validationRules: EntityDataValidation = {
			maxLength: tf.validation?.maxLength ?? null,
			regex: pattern ?? null,
		};
		return {
			...base,
			...tf,
			validationRules,
			dataType: "text",
		};
	}

	if (field.type === "number") {
		const nf = field as NumberField;
		const { apiNumberDataType: explicitKind, ...nfWithoutApiKind } = nf;
		const apiNumberDataType = explicitKind ?? API_NUMBER_DATA_TYPES.Decimal;
		const validationRules: EntityDataValidation = {
			rangeMin: nf.validation?.min ?? null,
			rangeMax: nf.validation?.max ?? null,
		};
		return {
			...base,
			...nfWithoutApiKind,
			validationRules,
			dataType: apiNumberDataType,
		};
	}

	if (field.type === "phone") {
		return {
			...base,
			...field,
			dataType: "phone",
			validationRules: EMPTY_ENTITY_DATA_VALIDATION,
		};
	}

	if (field.type === "url") {
		const uf = field as UrlField;
		const validationRules: EntityDataValidation = {
			maxLength: uf.validation?.maxLength ?? null,
			regex: uf.validation?.pattern ?? null,
		};
		return {
			...base,
			...uf,
			validationRules,
			dataType: "url",
		};
	}

	if (field.type === "select") {
		const sf = field as SelectField;
		const validationRules: EntityDataValidation = {
			validValues: sf.options.map((o) => o.value),
		};
		return {
			...base,
			...sf,
			validationRules,
			dataType: "text",
		};
	}

	if (field.type === "radio") {
		const validationRules: EntityDataValidation = {
			validValues: field.options.map((o) => o.value),
		};
		return {
			...base,
			...field,
			validationRules,
			dataType: "text",
		};
	}

	if (field.type === "checkbox") {
		const cf = field as CheckboxField;
		const validationRules: EntityDataValidation = {
			validValues: null,
		};
		return {
			...base,
			...cf,
			validationRules,
			dataType: "boolean",
		};
	}

	if (field.type === "toggleButton") {
		const tb = field as ToggleButtonField;
		const validationRules: EntityDataValidation = {
			validValues: tb.options.map((o) => o.value),
		};
		return {
			...base,
			...tb,
			validationRules,
			dataType: "toggleButton",
		};
	}

	if (field.type === "date") {
		return { ...base, ...field, dataType: API_DATE_ONLY_DATA_TYPE };
	}
	if (field.type === "time") {
		return { ...base, ...field, dataType: API_TIME_ONLY_DATA_TYPE };
	}

	if (field.type === "dateTime") {
		return { ...base, ...field, dataType: API_DATETIME_DATA_TYPE };
	}

	if (field.type === "file") {
		const fu = field as FileUploadField;
		return {
			...base,
			...fu,
			dataType: "file",
		};
	}

	if (field.type === "multiSelect") {
		return {
			...base,
			...field,
			dataType: "multiselect",
			validationRules: {
				validValues: field.options.map((option) => option.value),
			},
		};
	}

	if (field.type === "users") {
		return { ...base, ...field, dataType: "users" };
	}

	if (field.type === "signature") {
		return { ...base, ...field, dataType: "signature" };
	}

	if (field.type === "address") {
		return { ...base, ...field, dataType: "address" };
	}

	return {
		...base,
		...field,
		dataType: field.type,
	} as PersistedDynamicFieldDefinition;
}

/**
 * Reconstructs a {@link FormField} from a persisted definition for builder import and validators.
 *
 * @param def - Row from `fieldDefinitions`.
 * @param ctx - Optional layout-derived `fieldType` map or per-node override.
 * @returns `null` if the row cannot be mapped to a supported {@link FormField}.
 */
export function persistedDefinitionToFormField(
	def: PersistedDynamicFieldDefinition,
	ctx?: PersistedDefinitionToFormFieldContext,
): FormField | null {
	const label = def.label?.trim() ? def.label : def.name;
	const required = def.required ?? false;
	const disabled = def.disabled ?? false;
	const hidden = def.hidden ?? false;

	const rules = def.validationRules;
	const layoutFieldType = resolveLayoutFieldTypeFromContext(def, ctx);

	if (typeof def.dataType === "string" && isBooleanApiDataType(def.dataType)) {
		return booleanDataTypeToFormField(def);
	}

	switch (def.dataType) {
		case "text": {
			const validValues = nonEmptyValidValues(rules);
			const forcePlainText =
				layoutFieldType === "text" || layoutFieldType === "textarea";
			if (layoutFieldType === "radio") {
				const options =
					def.options ?? validValues.map((v) => ({ value: v, label: v }));
				return {
					id: def.id,
					name: def.name,
					label,
					type: "radio",
					required,
					disabled,
					hidden,
					hint: def.hint,
					options,
					defaultValue:
						typeof def.defaultValue === "string" ? def.defaultValue : undefined,
				} satisfies RadioField;
			}
			if (validValues.length > 0 && !forcePlainText) {
				const options =
					def.options ?? validValues.map((v) => ({ value: v, label: v }));
				return {
					id: def.id,
					name: def.name,
					label,
					type: "select",
					required,
					disabled,
					hidden,
					hint: def.hint,
					placeholder: def.placeholder,
					options,
					multiple: def.multiple ?? false,
					defaultValue:
						typeof def.defaultValue === "string" ? def.defaultValue : undefined,
				} satisfies SelectField;
			}
			const validation: TextField["validation"] = {};
			if (rules?.maxLength != null) validation.maxLength = rules.maxLength;
			if (rules?.regex) {
				validation.pattern = rules.regex;
			}
			const uiVal = def.validation;
			if (uiVal && typeof uiVal === "object" && "rows" in uiVal) {
				const raw = (uiVal as { rows?: unknown }).rows;
				if (typeof raw === "number" && Number.isFinite(raw)) {
					const clamped = Math.max(1, Math.min(50, Math.floor(raw)));
					validation.rows = clamped;
				}
			}
			const hasValidation = Object.keys(validation).length > 0;
			return {
				id: def.id,
				name: def.name,
				label,
				type: "text",
				required,
				disabled,
				hidden,
				hint: def.hint,
				placeholder: def.placeholder,
				validation: hasValidation ? validation : undefined,
			};
		}
		case "integer":
		case "Integer":
		case "decimal":
		case "Decimal":
		case "number":
			return {
				id: def.id,
				name: def.name,
				label,
				type: "number",
				apiNumberDataType: normalizeApiNumberDataType(def.dataType),
				required,
				disabled,
				hidden,
				hint: def.hint,
				placeholder: def.placeholder,
				validation:
					rules?.rangeMin != null || rules?.rangeMax != null
						? {
								min: rules.rangeMin ?? undefined,
								max: rules.rangeMax ?? undefined,
							}
						: undefined,
			} satisfies NumberField;
		case "phone":
		case "tel":
			return {
				id: def.id,
				name: def.name,
				label,
				type: "phone",
				required,
				disabled,
				hidden,
				hint: def.hint,
				placeholder: def.placeholder,
			};
		case "url": {
			const validation: UrlField["validation"] = {
				pattern: rules?.regex ?? PALETTE_URL_PATTERN,
			};
			if (rules?.maxLength != null) validation.maxLength = rules.maxLength;
			return {
				id: def.id,
				name: def.name,
				label,
				type: "url",
				required,
				disabled,
				hidden,
				hint: def.hint,
				placeholder: def.placeholder,
				validation,
			} satisfies UrlField;
		}
		case API_DATE_ONLY_DATA_TYPE:
		case "datePicker":
		case "dateOnly":
			return {
				id: def.id,
				name: def.name,
				label,
				type: "date",
				required,
				disabled,
				hidden,
				hint: def.hint,
				placeholder: def.placeholder,
			} satisfies DatePickerField;
		case API_TIME_ONLY_DATA_TYPE:
		case "timePicker":
		case "timeOnly":
			return {
				id: def.id,
				name: def.name,
				label,
				type: "time",
				required,
				disabled,
				hidden,
				hint: def.hint,
				placeholder: def.placeholder,
			} satisfies TimePickerField;
		case API_DATETIME_DATA_TYPE:
		case "dateTime":
		case "dateTimePicker":
			return {
				id: def.id,
				name: def.name,
				label,
				type: "dateTime",
				required,
				disabled,
				hidden,
				hint: def.hint,
				placeholder: def.placeholder,
			} satisfies DateTimePickerField;
		case "select": {
			const options =
				def.options ??
				rules?.validValues?.map((v) => ({ value: v, label: v })) ??
				[];
			return {
				id: def.id,
				name: def.name,
				label,
				type: "select",
				required,
				disabled,
				hidden,
				hint: def.hint,
				placeholder: def.placeholder,
				options,
				multiple: def.multiple ?? false,
				defaultValue:
					typeof def.defaultValue === "string" ? def.defaultValue : undefined,
			} satisfies SelectField;
		}
		case "radio": {
			const options =
				def.options ??
				rules?.validValues?.map((v) => ({ value: v, label: v })) ??
				[];
			return {
				id: def.id,
				name: def.name,
				label,
				type: "radio",
				required,
				disabled,
				hidden,
				hint: def.hint,
				options,
				defaultValue:
					typeof def.defaultValue === "string" ? def.defaultValue : undefined,
			};
		}
		case "toggleButton": {
			const options =
				def.options ??
				rules?.validValues?.map((v) => ({ value: v, label: v })) ??
				[];
			return {
				id: def.id,
				name: def.name,
				label,
				type: "toggleButton",
				required,
				disabled,
				hidden,
				hint: def.hint,
				options,
				exclusive: def.exclusive ?? true,
				defaultValue:
					typeof def.defaultValue === "string" ? def.defaultValue : undefined,
			} satisfies ToggleButtonField;
		}
		case "fileUpload":
		case "file":
			return {
				id: def.id,
				name: def.name,
				label,
				type: "file",
				required,
				disabled,
				hidden,
				presentation: def.presentation,
				hint: def.hint,
				accept: def.accept,
				multiple: def.multiple,
				dropZoneTitle: def.dropZoneTitle,
				dropZoneHint: def.dropZoneHint,
				validation: def.validation as FileUploadField["validation"],
			} satisfies FileUploadField;
		case "checkbox":
			return {
				id: def.id,
				name: def.name,
				label,
				type: "checkbox",
				required,
				disabled,
				hidden,
				hint: def.hint,
				defaultChecked: def.defaultChecked ?? false,
			} satisfies CheckboxField;
		case "multiselect":
		case "multiSelect": {
			const options =
				def.options ??
				rules?.validValues?.map((value) => ({ value, label: value })) ??
				[];
			return {
				id: def.id,
				name: def.name,
				label,
				type: "multiSelect",
				required,
				disabled,
				hidden,
				hint: def.hint,
				placeholder: def.placeholder,
				options,
			} satisfies MultiSelectField;
		}
		case "users":
			return {
				id: def.id,
				name: def.name,
				label,
				type: "users",
				required,
				disabled,
				hidden,
				hint: def.hint,
				placeholder: def.placeholder,
			};
		case "signature":
			return {
				id: def.id,
				name: def.name,
				label,
				type: "signature",
				required,
				disabled,
				hidden,
				hint: def.hint,
			};
		case "address":
			return {
				id: def.id,
				name: def.name,
				label,
				type: "address",
				required,
				disabled,
				hidden,
				hint: def.hint,
				placeholder: def.placeholder,
			};
		default:
			return null;
	}
}
